/**
 * Signup Tracking Utility
 *
 * Tracks signup events for analytics platforms
 * Helps identify users who start but don't complete signup for follow-up campaigns
 */

interface SignupTrackingData {
  email?: string;
  firstName?: string;
  lastName?: string;
  signupType: 'owner' | 'student';
  timestamp: string;
  completed: boolean;
}

const SIGNUP_TRACKING_KEY = 'rollops_signup_tracking';

/**
 * Track when user visits signup page
 */
export function trackSignupPageVisit(signupType: 'owner' | 'student') {
  try {
    // Store signup start in localStorage for tracking incomplete signups
    const trackingData: SignupTrackingData = {
      signupType,
      timestamp: new Date().toISOString(),
      completed: false,
    };
    localStorage.setItem(SIGNUP_TRACKING_KEY, JSON.stringify(trackingData));

    // Apollo tracking removed - will be replaced with proper analytics later

    // Track with Google Analytics if available
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (typeof window !== 'undefined' && (window as any).gtag) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).gtag('event', 'signup_page_visit', {
        signup_type: signupType,
      });
    }
  } catch (error) {
    // Error tracking signup page visit - silently fail
  }
}

/**
 * Track when user starts filling signup form
 */
export function trackSignupFormStart(signupType: 'owner' | 'student', email?: string) {
  try {
    const existingData = localStorage.getItem(SIGNUP_TRACKING_KEY);
    const trackingData: SignupTrackingData = existingData
      ? { ...JSON.parse(existingData), email }
      : {
          signupType,
          timestamp: new Date().toISOString(),
          completed: false,
          email,
        };
    
    localStorage.setItem(SIGNUP_TRACKING_KEY, JSON.stringify(trackingData));

    // Apollo tracking removed - will be replaced with proper analytics later

    // Track with Google Analytics if available
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (typeof window !== 'undefined' && (window as any).gtag) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).gtag('event', 'signup_form_start', {
        signup_type: signupType,
      });
    }
  } catch (error) {
    // Error tracking signup form start - silently fail
  }
}

/**
 * Track when user completes signup successfully
 */
export function trackSignupComplete(
  signupType: 'owner' | 'student',
  email: string,
  firstName?: string,
  lastName?: string,
) {
  try {
    // Mark as completed in localStorage
    const trackingData: SignupTrackingData = {
      signupType,
      email,
      firstName,
      lastName,
      timestamp: new Date().toISOString(),
      completed: true,
    };
    localStorage.setItem(SIGNUP_TRACKING_KEY, JSON.stringify(trackingData));

    // Apollo tracking removed - will be replaced with proper analytics later

    // Track with Google Analytics if available
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (typeof window !== 'undefined' && (window as any).gtag) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).gtag('event', 'signup_complete', {
        signup_type: signupType,
        email,
      });
    }

    // Clear tracking data after successful signup (or keep for a short period for analytics)
    // We'll keep it for 24 hours to allow for analytics processing
    setTimeout(() => {
      localStorage.removeItem(SIGNUP_TRACKING_KEY);
    }, 24 * 60 * 60 * 1000);
  } catch (error) {
    // Error tracking signup complete - silently fail
  }
}

/**
 * Track when user abandons signup (leaves page without completing)
 * This should be called on page unload or when navigating away
 */
export function trackSignupAbandon() {
  try {
    const existingData = localStorage.getItem(SIGNUP_TRACKING_KEY);
    if (!existingData) return; // No signup in progress

    const trackingData: SignupTrackingData = JSON.parse(existingData);
    
    // Only track if not already completed
    if (trackingData.completed) return;

    // Apollo tracking removed - will be replaced with proper analytics later

    // Track with Google Analytics if available
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (typeof window !== 'undefined' && (window as any).gtag) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).gtag('event', 'signup_abandon', {
        signup_type: trackingData.signupType,
      });
    }

    // Keep tracking data for 30 days for follow-up campaigns
    // Don't remove immediately - Apollo needs time to process
  } catch (error) {
    // Error tracking signup abandon - silently fail
  }
}

/**
 * Get incomplete signup data (for debugging or manual follow-up)
 */
export function getIncompleteSignupData(): SignupTrackingData | null {
  try {
    const data = localStorage.getItem(SIGNUP_TRACKING_KEY);
    if (!data) return null;

    const trackingData: SignupTrackingData = JSON.parse(data);
    return trackingData.completed ? null : trackingData;
  } catch {
    return null;
  }
}



